import { Cell, createColumnHelper } from "@tanstack/react-table";
import merge from "lodash/merge";
import { Fragment } from "react";

import { FormatDate } from "@/components/Date";
import { LineItemUOMQuantity } from "@/features/job-tracking/components/LineItemUOMQuantity";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { Build, BuildLineItem } from "../../queries/tracking-data/builds";
import { BinCell } from "../BinCell";
import { LineItemDescription } from "../LineItemDescription";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";

export interface BuildTableRow {
  rowId: string;
  build: Build;
  input?: BuildLineItem;
  output?: BuildLineItem;
  numLines: number;
  isFirstLine: boolean;
}

// For build/output columns, set the rowspan of each cell on the on the first line to the total number of lines. Hide all subsequent rows for the same receipt
const buildColCellProps = (
  cell: Cell<BuildTableRow, unknown>,
  additionalProps?: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "rowSpan">,
) => {
  const { numLines, isFirstLine } = cell.row.original;
  return isFirstLine ?
      { ...additionalProps, rowSpan: numLines }
    : { ...additionalProps, className: "d-none " + (additionalProps?.className ?? "") };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<BuildTableRow>();
const buildAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: buildColCellProps } }, column));
const outputAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: buildColCellProps }, enableSorting: false }, column));
const inputAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ enableSorting: false }, column));

const buildColumns = [
  buildAccessorCol("build.number", {
    header: "Build #",
    cell: ({ getValue, row }) => <SosEntityLink type="build" id={row.original.build.id} label={getValue()} />,
  }),
  buildAccessorCol("build.date", {
    header: "Date",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  buildAccessorCol("build.linkedWOs", {
    header: "Linked WO",
    cell: ({ row, getValue }) =>
      getValue().map((wo, index) => (
        <Fragment key={wo.id}>
          {index > 0 && ", "}
          <JobTrackingInternalLink trackerPath="work-orders">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityLineItemHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="workorder"
                id={wo.id}
                lineNumber={0}
                label={wo.number}
              />
            )}
          </JobTrackingInternalLink>
        </Fragment>
      )),
  }),
  buildAccessorCol("build.workCenter.name", {
    header: "Work Center",
    cell: ({ getValue }) => {
      const workCenter = getValue();
      const variant = workCenter === "Finished Goods" ? "success" : "warning";
      return <span className={`text-${variant}`}>{workCenter}</span>;
    },
  }),
];

const outputColumns = [
  outputAccessorCol("output.item.name", {
    header: "Assembly",
    cell: ({ row }) => {
      const output = row.original.output;
      return (
        output && (
          <JobTrackingInternalLink trackerPath="bom-by-component">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="item"
                id={output.item.id}
                label={output.item.name}
              />
            )}
          </JobTrackingInternalLink>
        )
      );
    },
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: (cell) => buildColCellProps(cell, { className: "border-start-double" }),
    },
  }),
  outputAccessorCol("output.item.description", {
    header: "Description",
    cell: ({ row }) => row.original.output && <LineItemDescription line={row.original.output} />,
  }),
  outputAccessorCol("output.uomQuantity", {
    header: "Quantity",
    cell: ({ row }) => row.original.output && <LineItemUOMQuantity line={row.original.output} />,
  }),
  outputAccessorCol("output.bin.number", {
    header: "To Bin",
    cell: ({ row }) => row.original.output && <BinCell bin={row.original.output.bin} />,
  }),
];

const inputColumns = [
  inputAccessorCol("input.lineNumber", {
    header: "Line #",
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: { className: "border-start-double" },
    },
  }),
  inputAccessorCol("input.item.name", {
    header: "Item",
    cell: ({ row }) => {
      const input = row.original.input;
      return (
        input && (
          <JobTrackingInternalLink trackerPath="bom-by-component">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="item"
                id={input.item.id}
                label={input.item.name}
              />
            )}
          </JobTrackingInternalLink>
        )
      );
    },
  }),
  inputAccessorCol("input.item.type", { header: "Type" }),
  inputAccessorCol("input.item.description", {
    header: "Description",
    cell: ({ row }) => row.original.input && <LineItemDescription line={row.original.input} />,
  }),
  inputAccessorCol("input.uomQuantity", {
    header: "Quantity",
    cell: ({ row }) => row.original.input && <LineItemUOMQuantity line={row.original.input} />,
  }),
  inputAccessorCol("input.bin.number", {
    header: "From Bin",
    cell: ({ row }) => row.original.input && <BinCell bin={row.original.input.bin} />,
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const buildTableColumns = [
  columnHelper.group({ header: "Builds", columns: buildColumns }),
  columnHelper.group({
    header: "Output",
    columns: outputColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
  columnHelper.group({
    header: "Inputs",
    columns: inputColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
];
